import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cargoApi, type CargoCustomer } from '@/api/cargo';
import { PageHead, DataTable, Drawer, SectionCard, Field, StatusBadge, type Col } from '@/shared/ui/kit';
import { useToast } from '@/app/providers/ToastProvider';
import { DELIVERY_METHOD, toOptions } from '@/shared/cargo';

const empty = { WarehouseCode: '', Name: '', Phone: '', Email: '', Address: '', DefaultDeliveryMethod: 0, IsActive: true, Note: '' };

export function CustomersPage() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<CargoCustomer | null | undefined>(undefined);
  const [form, setForm] = useState({ ...empty });
  const [busy, setBusy] = useState(false);

  const q = useQuery({ queryKey: ['cargo', 'customers'], queryFn: cargoApi.customers });

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (q.data ?? []).filter((r) => !s || r.Name.toLowerCase().includes(s) || r.WarehouseCode.toLowerCase().includes(s) || r.Phone.includes(s));
  }, [q.data, search]);

  function open(c: CargoCustomer | null) {
    setEdit(c);
    setForm(c ? { WarehouseCode: c.WarehouseCode, Name: c.Name, Phone: c.Phone, Email: c.Email ?? '', Address: c.Address ?? '', DefaultDeliveryMethod: c.DefaultDeliveryMethod, IsActive: c.IsActive, Note: c.Note ?? '' } : { ...empty });
  }

  async function save() {
    if (!form.Name.trim() || !form.Phone.trim()) return toast('Нэр, утас оруулна уу.', 'warning');
    setBusy(true);
    try {
      await cargoApi.saveCustomer(edit ? edit.Id : null, { ...form, WarehouseCode: form.WarehouseCode || null });
      toast('Хадгаллаа.', 'success');
      setEdit(undefined);
      q.refetch();
    } catch (e) {
      toast((e as Error).message, 'danger');
    } finally {
      setBusy(false);
    }
  }

  const cols: Col<CargoCustomer>[] = [
    { key: 'WarehouseCode', label: 'Агуулахын код', width: 150, render: (r) => <button className="ct-link" onClick={(e) => { e.stopPropagation(); open(r); }}>{r.WarehouseCode}</button> },
    { key: 'Name', label: 'Нэр', render: (r) => r.Name },
    { key: 'Phone', label: 'Утас', render: (r) => r.Phone },
    { key: 'DefaultDeliveryMethod', label: 'Хүргэлт', render: (r) => r.DefaultDeliveryMethodLabel },
    { key: 'IsActive', label: 'Төлөв', render: (r) => <StatusBadge label={r.IsActive ? 'Идэвхтэй' : 'Идэвхгүй'} color={r.IsActive ? 'var(--ts-ok)' : 'var(--ts-muted)'} /> },
  ];

  return (
    <div>
      <PageHead title="Харилцагч" subtitle="Карго харилцагч + хувийн агуулахын код."
        actions={<button className="ts-btn ts-btn-primary" onClick={() => open(null)}><i className="bi bi-plus-lg" /> Шинэ</button>} />
      <div style={{ position: 'relative', marginBottom: 14, maxWidth: 360 }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: 10, color: 'var(--ts-muted)' }} />
        <input className="ts-input" style={{ paddingLeft: 34 }} placeholder="Нэр, код, утас хайх..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <DataTable cols={cols} rows={rows} onRowClick={open} empty="Харилцагч алга." />

      {edit !== undefined && (
        <Drawer title={edit ? edit.Name : 'Шинэ харилцагч'} open onClose={() => setEdit(undefined)}
          footer={<><button className="ts-btn ts-btn-ghost" onClick={() => setEdit(undefined)}>Болих</button><button className="ts-btn ts-btn-primary" disabled={busy} onClick={save}><i className="bi bi-check2" /> Хадгалах</button></>}>
          <SectionCard icon="bi-person" title="Үндсэн мэдээлэл" hint={edit ? undefined : 'Код хоосон бол автомат үүснэ'}>
            <div className="ts-row">
              <div style={{ gridColumn: 'span 4' }}><Field label="Агуулахын код"><input className="ts-input" value={form.WarehouseCode} onChange={(e) => setForm({ ...form, WarehouseCode: e.target.value })} placeholder="(автомат)" /></Field></div>
              <div style={{ gridColumn: 'span 8' }}><Field label="Нэр" required><input className="ts-input" required value={form.Name} onChange={(e) => setForm({ ...form, Name: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 4' }}><Field label="Утас" required><input className="ts-input" required value={form.Phone} onChange={(e) => setForm({ ...form, Phone: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 8' }}><Field label="И-мэйл"><input className="ts-input" value={form.Email} onChange={(e) => setForm({ ...form, Email: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 8' }}><Field label="Хаяг"><input className="ts-input" value={form.Address} onChange={(e) => setForm({ ...form, Address: e.target.value })} /></Field></div>
              <div style={{ gridColumn: 'span 4' }}><Field label="Үндсэн хүргэлт"><select className="ts-select" value={form.DefaultDeliveryMethod} onChange={(e) => setForm({ ...form, DefaultDeliveryMethod: Number(e.target.value) })}>{toOptions(DELIVERY_METHOD).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></Field></div>
              <div style={{ gridColumn: 'span 12' }}><label className="ts-flex-row" style={{ cursor: 'pointer' }}><input type="checkbox" checked={form.IsActive} onChange={(e) => setForm({ ...form, IsActive: e.target.checked })} /> Идэвхтэй</label></div>
            </div>
          </SectionCard>
        </Drawer>
      )}
    </div>
  );
}
